import './SelectField.scss';

import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import HtmlElement from '@/common/components/atoms/HtmlElement/HtmlElement';
import Select from '@/common/components/atoms/Select/Select';

import FieldErrors from '../FieldErrors/FieldErrors';

const SelectField = ({
  children,
  className,
  context,
  disabled,
  errors,
  errorsIcon,
  icon,
  id,
  name,
  onBlur,
  onChange,
  onClick,
  options,
  placeholder,
  position,
  readOnly,
  required,
  value,
}) => {
  const classNames = useMemo(
    () =>
      [
        'select-field',
        disabled ? 'select-field--disabled' : null,
        readOnly ? 'select-field--readonly' : null,
        errors && errors.filter((val) => val).length
          ? 'select-field--error'
          : null,
        `select-field--${position}`,
        required ? 'select-field--required' : '',
        className,
      ]
        .filter((val) => val)
        .join(' '),
    [className, disabled, errors, position, readOnly, required],
  );

  return (
    <div className={classNames} id={id} data-qa-id="select-field">
      <HtmlElement
        className="select-field__label"
        htmlFor={`${id}-select`}
        id={`${id}-label`}
        tag="label"
      >
        {children}
      </HtmlElement>
      <div className="select-field__select" data-qa-id="select-field-select">
        <Select
          context={context}
          disabled={disabled}
          icon={icon}
          id={`${id}-select`}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          onClick={onClick}
          options={options}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          value={value}
        />
      </div>
      {errors && (
        <FieldErrors
          className="select-field__errors"
          errors={errors}
          icon={errorsIcon}
          name={name}
        />
      )}
    </div>
  );
};

SelectField.defaultProps = {
  children: null,
  className: '',
  context: null,
  disabled: false,
  errors: null,
  errorsIcon: null,
  icon: null,
  id: null,
  name: null,
  onBlur: () => {},
  onChange: () => {},
  onClick: () => {},
  placeholder: '',
  position: 'top',
  readOnly: false,
  required: false,
  value: null,
};

SelectField.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  className: PropTypes.string,
  context: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.string),
  errorsIcon: PropTypes.element,
  icon: PropTypes.element,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  position: PropTypes.oneOf(['top', 'right', 'left']),
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SelectField;
